import type { ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { toneColor, toneSoft } from './viz/tokens';

// =========================================================================
// OEM tile vocabulary — the demo's `.card.kpi` and `.ctkpi`, as components.
// Fork-owned (zone C), created by the P3a lane and shared by Asset360,
// Approvals and Cost summary.
//
// This is deliberately NOT in `src/design-system/`. The design system is
// foundation-upstream (zone A) and its cards carry the foundation's glass
// vocabulary; these carry the demo's — a tone rail, an uppercase eyebrow, a
// large tabular figure with a unit hung off it, a detail line. Putting a
// product-specific recipe into the shared design system is how a foundation
// stops being reusable.
//
// One rule holds throughout: **a measure that can be drilled into is a
// `<button>`**, never a `<div onClick>`. Every tile on the landing page opens
// something, so every tile is keyboard-reachable and announces itself.
// =========================================================================

/** The demo's three-way KPI tone, plus the neutral default. */
export type MeasureTone = 'good' | 'warn' | 'bad' | 'flat';

export interface MeasureTileProps {
  /** Set by `<Commentable>` (cloneElement); forwarded to the root element so the anchor reaches the DOM. */
  'data-comment-anchor'?: string;
  /** Uppercase eyebrow naming the measure. */
  eyebrow: string;
  /** The figure. Already formatted — the tile does no arithmetic. */
  value: ReactNode;
  /** What the figure is, hung beside it at small size. */
  unit?: string;
  /** The line under the figure. */
  detail?: ReactNode;
  tone?: MeasureTone;
  /** Makes the tile a button. Omit for a read-only measure. */
  onSelect?: () => void;
  /** What activating the tile does, for assistive tech. */
  selectHint?: string;
  className?: string;
}

/**
 * One control-tower / KPI measure.
 *
 * The tone shows as a 3px rail down the left edge rather than as coloured
 * text: at 28px the figure is the loudest thing on the tile already, and
 * tinting it as well pushes the "warn" amber under the 4.5:1 contrast floor
 * on the light canvas. The rail carries the same information at full
 * saturation without being asked to be legible as text.
 */
export function MeasureTile({
  eyebrow,
  value,
  unit,
  detail,
  tone = 'flat',
  onSelect,
  selectHint,
  className,
  'data-comment-anchor': commentAnchor,
}: MeasureTileProps) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[3px] rounded-l-xl"
        style={{ background: toneColor(tone) }}
      />
      <span
        className="block text-[10px] font-semibold uppercase leading-4 tracking-[0.1em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {eyebrow}
      </span>
      <span className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
        <span
          className="mono text-[26px] font-semibold leading-none tracking-tight"
          style={{ color: 'var(--ink-primary)' }}
        >
          {value}
        </span>
        {unit ? (
          <span className="text-[11.5px] leading-tight" style={{ color: 'var(--ink-secondary)' }}>
            {unit}
          </span>
        ) : null}
      </span>
      {detail ? (
        <span
          className="mt-2 block text-[11.5px] leading-[1.5]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          {detail}
        </span>
      ) : null}
    </>
  );

  const shell = cn(
    'relative block min-w-0 overflow-hidden rounded-xl border p-4 text-left',
    className,
  );
  const style = {
    borderColor: 'var(--hairline)',
    background: 'var(--surface-2)',
  } as const;

  if (!onSelect) {
    return (
      <div data-comment-anchor={commentAnchor} className={shell} style={style}>
        {body}
      </div>
    );
  }

  return (
    <button
      data-comment-anchor={commentAnchor}
      type="button"
      onClick={onSelect}
      aria-label={selectHint ? `${eyebrow}. ${selectHint}` : undefined}
      className={cn(
        shell,
        'transition-[background-color,box-shadow,transform] hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] active:translate-y-px',
      )}
      style={style}
    >
      {body}
    </button>
  );
}

/**
 * A tone chip — the demo's `.pill.good` / `.warn` / `.bad`.
 *
 * Uses the OEM status tokens rather than the design system's `Pill`, which is
 * built on Tailwind's zinc/amber/red ramps and would drift from the condition
 * bands sitting next to it.
 */
export function TonePill({
  tone = 'flat',
  children,
  className,
}: {
  tone?: MeasureTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-px text-[10.5px] font-medium leading-[1.6]',
        className,
      )}
      style={{ background: toneSoft(tone), color: toneColor(tone) }}
    >
      {children}
    </span>
  );
}

/**
 * A condition-band chip. `band` is one of the five `ConditionBand` values, so
 * the class comes straight off the data (`styles/oem.css` § band recipes).
 */
export function BandPill({ band, children }: { band: string; children: ReactNode }) {
  return <span className={`band-pill band-${band}`}>{children}</span>;
}

/** A section band on an OEM scene — heading, subtitle, trailing link slot. */
export function SceneSection({
  title,
  subtitle,
  actions,
  children,
  className,
  'data-comment-anchor': commentAnchor,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Set by `<Commentable>` (cloneElement); forwarded to the root element so the anchor reaches the DOM. */
  'data-comment-anchor'?: string;
}) {
  return (
    <section data-comment-anchor={commentAnchor} className={cn('min-w-0', className)}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2
          className="text-[15px] font-semibold tracking-tight"
          style={{ color: 'var(--ink-primary)' }}
        >
          {title}
          {subtitle ? (
            <span className="font-normal" style={{ color: 'var(--ink-tertiary)' }}>
              {' · '}
              {subtitle}
            </span>
          ) : null}
        </h2>
        {actions ? <div className="flex items-center gap-3">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * The "open the rows behind this" affordance — the demo's `button.src`.
 *
 * Underlined on hover rather than always, because a page with forty of these
 * reads as a wall of links otherwise; the dotted underline at rest is enough
 * to say "this is a control" without competing with the figures.
 */
export function SourceLink({
  onClick,
  children,
  className,
}: {
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px] transition-colors hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
        className,
      )}
      style={{ color: 'var(--accent)' }}
    >
      {children}
    </button>
  );
}
